import React, { useState } from "react";
import { Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from "@mui/material";
import { Delete, Edit, InfoOutlined, OpenInNew } from "mosaic-react-icons";
import { SovosBox, SovosButton, SovosIconButton, SovosTypography } from "mosaic-react";

const LOCALE_LABELS = {
    CSV_PT: "Portugal",
    CSV_RO: "Romania",
    CSV_ES: "Spain",
    CSV_LT: "Lithuania",
    CSV_FR: "France",
};

const EmptyState = ({ locale, history }) => (
    <SovosBox
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
        }}
    >
        <SovosTypography variant="h6" color="text.secondary">
            No translation tables linked to import profiles
        </SovosTypography>
        <SovosTypography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 460 }}>
            Link a translation table from the Data Validation step in the Column Mapping wizard,
            or manage existing tables in Settings.
        </SovosTypography>
        <SovosButton
            variant="outlined"
            size="small"
            endIcon={<OpenInNew fontSize="small" />}
            onClick={() => history.push(`/${locale}/settings/translations_table`)}
        >
            Settings → Translation Tables
        </SovosButton>
    </SovosBox>
);

const TableCard = ({ table, onEditRules, onUnlink }) => (
    <SovosBox
        sx={{
            display: "flex",
            alignItems: "center",
            px: 2.5,
            py: 1.75,
            borderBottom: "1px solid",
            borderColor: "divider",
            gap: 2,
            "&:hover": { bgcolor: "action.hover" },
            transition: "background 0.15s",
        }}
    >
        <SovosBox sx={{ flex: "0 0 36px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SovosBox
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                }}
            />
        </SovosBox>

        <SovosBox sx={{ flex: 1, minWidth: 0 }}>
            <SovosTypography variant="body2" fontWeight={600} noWrap>
                {table.name}
            </SovosTypography>
            {table.description && (
                <SovosTypography variant="caption" color="text.secondary" noWrap>
                    {table.description}
                </SovosTypography>
            )}
        </SovosBox>

        <SovosBox sx={{ flex: "0 0 auto" }}>
            <Chip
                label={LOCALE_LABELS[table.datasetLocale] || table.datasetLocale}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
            />
        </SovosBox>

        <SovosBox sx={{ display: "flex", gap: 0.5, flex: "0 0 auto" }}>
            <Tooltip title="Edit rules in Translation Builder">
                <SovosIconButton size="small" onClick={() => onEditRules(table.id)}>
                    <Edit fontSize="small" />
                </SovosIconButton>
            </Tooltip>
            <Tooltip title="Unlink from this profile">
                <SovosIconButton size="small" onClick={() => onUnlink(table)} sx={{ color: "error.main" }}>
                    <Delete fontSize="small" />
                </SovosIconButton>
            </Tooltip>
        </SovosBox>
    </SovosBox>
);

const TranslationTablesMappingView = ({
    tables = [],
    loading,
    locale,
    projectId,
    history,
    onUnlink,
}) => {
    const [unlinkTarget, setUnlinkTarget] = useState(null);
    const [unlinking, setUnlinking] = useState(false);

    const handleEditRules = (translationId) => {
        history.push(`/${locale}/settings/translations_table/builder/${translationId}`);
    };

    const handleUnlinkConfirm = async () => {
        if (!unlinkTarget) return;
        setUnlinking(true);
        await onUnlink(unlinkTarget.id, parseInt(projectId, 10));
        setUnlinking(false);
        setUnlinkTarget(null);
    };

    if (!loading && tables.length === 0) {
        return <EmptyState locale={locale} history={history} />;
    }

    return (
        <>
            <SovosBox sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <SovosBox
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2.5,
                        py: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        bgcolor: "grey.50",
                    }}
                >
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <SovosTypography variant="subtitle2" color="text.secondary">
                            {tables.length} table{tables.length !== 1 ? "s" : ""} linked to import profiles
                        </SovosTypography>
                        <Tooltip title="Tables are linked via the Data Validation step in Column Mapping. They can be applied to fix staging data before import.">
                            <InfoOutlined sx={{ fontSize: 16, color: "text.disabled", cursor: "help" }} />
                        </Tooltip>
                    </SovosBox>
                    <SovosButton
                        variant="text"
                        size="small"
                        endIcon={<OpenInNew fontSize="small" />}
                        onClick={() => history.push(`/${locale}/settings/translations_table`)}
                    >
                        Manage all in Settings
                    </SovosButton>
                </SovosBox>

                <SovosBox
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "36px 1fr auto auto",
                        px: 2.5,
                        py: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <SovosBox />
                    <SovosTypography variant="caption" fontWeight={600} color="text.secondary">
                        Name
                    </SovosTypography>
                    <SovosTypography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 8 }}>
                        Locale
                    </SovosTypography>
                    <SovosTypography variant="caption" fontWeight={600} color="text.secondary">
                        Actions
                    </SovosTypography>
                </SovosBox>

                <SovosBox sx={{ overflowY: "auto", flex: 1 }}>
                    {tables.map((table) => (
                        <TableCard
                            key={table.id}
                            table={table}
                            locale={locale}
                            onEditRules={handleEditRules}
                            onUnlink={setUnlinkTarget}
                        />
                    ))}
                </SovosBox>
            </SovosBox>

            <Dialog
                open={!!unlinkTarget}
                onClose={() => setUnlinkTarget(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Unlink translation table?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <strong>{unlinkTarget?.name}</strong> will be removed from this project's import profiles.
                        The table itself and its rules are not deleted — it can be re-linked from the Data Validation step.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <SovosButton variant="text" onClick={() => setUnlinkTarget(null)} disabled={unlinking}>
                        Cancel
                    </SovosButton>
                    <SovosButton
                        variant="contained"
                        color="error"
                        onClick={handleUnlinkConfirm}
                        disabled={unlinking}
                    >
                        {unlinking ? "Unlinking…" : "Unlink"}
                    </SovosButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TranslationTablesMappingView;
